import { useState } from "react";
import {
  AlertTriangle,
  XCircle,
  HelpCircle,
  Wrench,
  ChevronRight,
} from "lucide-react";
import type {
  DataWarning,
  AgentError,
  CannotAnswer,
  ToolRequest,
} from "@/lib/api/assistantV2";

function hasDetails(details: Record<string, unknown> | undefined | null): boolean {
  return details != null && Object.keys(details).length > 0;
}

// --- Run-level warnings (DataWarning[]). FIELD_NOT_POPULATED -> "DATA NOTE". --

export function WarningsPanelV2({ warnings }: { warnings: DataWarning[] }) {
  const [open, setOpen] = useState(false);
  if (warnings.length === 0) return null;
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-amber-900 hover:bg-amber-100"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Warnings
          <span className="text-xs text-amber-700">({warnings.length})</span>
        </div>
        <ChevronRight
          className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="divide-y divide-amber-100 border-t border-amber-200">
          {warnings.map((w, i) => (
            <WarningRow key={i} warning={w} />
          ))}
        </div>
      )}
    </div>
  );
}

function WarningRow({ warning }: { warning: DataWarning }) {
  const [open, setOpen] = useState(false);
  const isDataNote = warning.code === "FIELD_NOT_POPULATED";
  return (
    <div className="px-4 py-2 text-sm text-amber-900">
      <div className="flex items-center gap-2">
        {isDataNote && (
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border border-amber-300 bg-white text-amber-700">
            data note
          </span>
        )}
        <span>{warning.message}</span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-[11px] text-amber-700 font-mono">{warning.code}</span>
        {hasDetails(warning.details) && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-0.5 text-[11px] text-amber-600 hover:text-amber-800"
          >
            <ChevronRight
              className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`}
            />
            details
          </button>
        )}
      </div>
      {open && (
        <pre className="mt-1 text-[11px] bg-white border border-amber-200 rounded p-2 overflow-x-auto text-amber-900">
          {JSON.stringify(warning.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

// --- The single run-level error (status === "error"), plus the plan source. --

export function ErrorPanelV2({
  error,
  code,
}: {
  error: AgentError;
  code?: string | null;
}) {
  const [showCode, setShowCode] = useState(false);
  return (
    <div className="border border-rose-200 bg-rose-50 rounded-lg overflow-hidden">
      <div className="px-4 py-2 flex items-center gap-2 text-sm font-medium text-rose-900 border-b border-rose-200">
        <XCircle className="h-4 w-4" />
        Error
      </div>
      <div className="px-4 py-3 text-sm text-rose-900 space-y-1">
        <div>{error.message}</div>
        <div className="text-[11px] text-rose-700 font-mono">{error.code}</div>
        {hasDetails(error.details) && (
          <pre className="mt-1 text-[11px] bg-white border border-rose-200 rounded p-2 overflow-x-auto text-rose-900">
            {JSON.stringify(error.details, null, 2)}
          </pre>
        )}
        {code && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowCode((v) => !v)}
              className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-rose-600 hover:text-rose-800"
            >
              <ChevronRight
                className={`h-3 w-3 transition-transform ${showCode ? "rotate-90" : ""}`}
              />
              Generated plan code
            </button>
            {showCode && (
              <pre className="mt-1 text-[11px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto">
                {code}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- The agent declined to answer (status === "cannot_answer"). --------------

const CANNOT_ANSWER_LABEL: Record<CannotAnswer["kind"], string> = {
  missing_info: "Needs more info",
  off_topic: "Off topic",
  ambiguous: "Ambiguous",
};

export function CannotAnswerPanel({
  cannotAnswer,
}: {
  cannotAnswer: CannotAnswer;
}) {
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden">
      <div className="px-4 py-2 flex items-center gap-2 text-sm font-medium text-amber-900 border-b border-amber-200">
        <HelpCircle className="h-4 w-4" />
        Can't answer
        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border border-amber-300 bg-white text-amber-700">
          {CANNOT_ANSWER_LABEL[cannotAnswer.kind] ?? cannotAnswer.kind}
        </span>
      </div>
      <div className="px-4 py-3 text-sm text-amber-900">
        {cannotAnswer.message}
      </div>
    </div>
  );
}

// --- The agent needs a tool that isn't available (status === "needs_tool"). --

export function ToolRequestPanel({ toolRequest }: { toolRequest: ToolRequest }) {
  return (
    <div className="border border-sky-200 bg-sky-50 rounded-lg overflow-hidden">
      <div className="px-4 py-2 flex items-center gap-2 text-sm font-medium text-sky-900 border-b border-sky-200">
        <Wrench className="h-4 w-4" />
        Tool requested
        <span className="font-mono text-xs text-sky-700">{toolRequest.name}</span>
      </div>
      <div className="px-4 py-3 text-sm text-sky-900 space-y-1.5">
        <div>{toolRequest.purpose}</div>
        <div className="text-[11px] font-mono text-sky-700 break-all">
          {toolRequest.signature}
        </div>
        {toolRequest.tried && (
          <div className="text-xs text-sky-800">
            <span className="text-sky-600">Tried: </span>
            {toolRequest.tried}
          </div>
        )}
      </div>
    </div>
  );
}
